import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { YandexMap } from "@common/yandex/provider/YandexMapProvider";
import { LOCATION, YmapUpdateEvent } from "../types";
import { Container } from "@mui/material";
import { CustomMarkerWithPopup } from "./CustomMarkerWithPopup";
import { useMapSearchEvents } from "@shared/map-events/useMapSearchEvents";
import { z } from "zod";
import { schema } from "../schema";
import { SearchEventDto } from "@app/data/tribeSwaggerApi";
import type { LngLat, LngLatBounds, YMapLocationRequest } from "ymaps3";
import useGeoProvider from "@shared/GeoProvider";
import { useTheme } from "@hooks/useTheme";
import style from "../style.module.css";
import { mapCache } from "@shared/map-events/mapCache";

export type FeedYMapContentProps = {
  ydata: YandexMap
}
type EventsValidModel = z.infer<typeof schema>

// Европейская часть России + Урал — там почти все события. Порядок: [долгота, широта]
const ALL_RUSSIA_VIEW: YMapLocationRequest = { center: [50, 56], zoom: 4 }

export const FeedYMapContent: FC<FeedYMapContentProps> = ({ ydata }) => {
  const { isDarkTheme } = useTheme();
  const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapListener } = ydata.yandexMap;
  const { onViewChange, data, isLoading, isAllRussia } = useMapSearchEvents();
  const [currentLocation] = useGeoProvider();
  const cityKey = isAllRussia ? "ALL" : `${currentLocation?.latitude}:${currentLocation?.longitude}`
  // Вернулись на карту в том же городе — открываемся там, где пользователь её оставил
  const restored = useRef(!!mapCache.view && mapCache.cityKey === cityKey)
  const [location, setLocation] = useState<YMapLocationRequest>(
    restored.current ? ({ center: mapCache.view!.center, zoom: mapCache.view!.zoom } as YMapLocationRequest) : LOCATION
  );
  // Текущий вид карты — от него зависит кластеризация. Обновляем только когда карта остановилась.
  const [view, setView] = useState<{ zoom: number; bounds?: LngLatBounds }>(
    restored.current ? { zoom: mapCache.view!.zoom, bounds: mapCache.view!.bounds } : { zoom: Number((LOCATION as any).zoom) || 9 }
  );
  const cityKeyRef = useRef(cityKey);
  cityKeyRef.current = cityKey;
  const lastZoom = useRef(view.zoom);

  const updateHandler = useCallback((e: YmapUpdateEvent) => {
    if (e.mapInAction) return // пока пользователь тянет карту — ничего не пересчитываем, отсюда были лаги
    const { zoom, bounds } = e.location
    lastZoom.current = zoom
    setView({ zoom, bounds })
    mapCache.view = { center: (e.location as any).center, zoom, bounds }
    mapCache.cityKey = cityKeyRef.current
    onViewChange(e.location)
  }, [onViewChange]);

  // Смена города — перелёт карты
  useEffect(() => {
    if (!currentLocation) return
    // Первое открытие после возврата — не перелетаем к центру города, остаёмся на прежнем месте
    if (restored.current) { restored.current = false; return }
    if (isAllRussia) {
      setLocation({ ...ALL_RUSSIA_VIEW, duration: 400 } as YMapLocationRequest)
      setView(v => ({ ...v, zoom: 4, bounds: undefined }))
    } else {
      setLocation({ center: [currentLocation.longitude, currentLocation.latitude], zoom: 12, duration: 400 } as YMapLocationRequest)
      setView(v => ({ ...v, zoom: 12, bounds: undefined }))
    }
  }, [currentLocation?.latitude, currentLocation?.longitude, isAllRussia]);

  // Клик по кластеру — приближаемся к нему
  const zoomTo = useCallback((center: LngLat, zoom: number) => {
    setLocation({ center, zoom, duration: 300 } as YMapLocationRequest)
  }, []);

  const events = useMemo(() => toValidModels(data), [data]);

  return (
    <Container sx={{
      height: "calc(100vh - 220px)",
      borderRadius: "16px",
      overflow: "hidden",
      position: "relative",
      "&.MuiContainer-root": { padding: 0 }
    }}>
      <YMap showScaleInCopyrights={false} theme={isDarkTheme ? "dark" : "light"} location={location}>
        <YMapDefaultSchemeLayer />
        <YMapDefaultFeaturesLayer />
        <CustomMarkerWithPopup events={events} zoom={view.zoom} bounds={view.bounds} onZoomTo={zoomTo} />
        <YMapListener onUpdate={updateHandler} />
      </YMap>
      {isLoading && <div className={style.mapLoading}>Загружаем события…</div>}
    </Container>
  );
};

// Проверяем схемой и берём РЕЗУЛЬТАТ проверки (с подставленными значениями по умолчанию)
const toValidModels = (data: SearchEventDto[] | undefined): EventsValidModel[] => {
  if (!data) return [];
  const out: EventsValidModel[] = [];
  for (const dto of data) {
    const r = schema.safeParse(dto);
    if (r.success) out.push(r.data);
  }
  return out;
};
